import { Box, Button as ThemeUiButton, useThemeUI } from "theme-ui";
import { useDispatch, useSelector } from "react-redux";
import { selectUserChoiceValues, selectUIVersion } from "store/app";
import { updateUserChoiceValues } from "store/app/appSlice";
import isNil from "lodash/fp/isNil";

import { SvgOrImg, Button } from "components";
import ToolTip from "components/ToolTip";
import * as styles from "./IconButtonOptions.styles";
import { CheckmarkCircleIcon } from "components/icons";
import { useDefaultValuesOnFirstRender } from "hooks";

export default function IconButtonOptions({
  name,
  options = [],
  value: selectedValue,
  onChange,
  backButton,
  defaultValueToSetOnRender,
  resetButtonOption,
}) {
  const {
    theme: { isMobile },
  } = useThemeUI();

  const dispatch = useDispatch();
  const userChoiceValues = useSelector(selectUserChoiceValues);

  const UIVersion = useSelector(selectUIVersion);

  useDefaultValuesOnFirstRender({
    options,
    defaultValueToSetOnRender,
    onChange,
    selectedValue,
  });

  const optionButtons = options.map((option) => {
    const isSelected = selectedValue === option.value;
    const isPremium = option.premium ? true : false;
    const premium = option.premiumValue;
    const newColor = option.newColor ? true : false;
    const colorValue = option.color;
    return {
      key: option.value,
      toolTip: !isMobile && option?.toolTip,
      component: (
        <ThemeUiButton
          key={option.value}
          variant="unstyled"
          aria-label={option?.label}
          onClick={() => onChange(option.value)}
          sx={
            isMobile && option?.toolTip?.component === "ColorPickerToolTip"
              ? styles.mobileColorDots({
                  colorDot: option.toolTip.props.backgroundColor,
                })
              : null
          }
        >
          {option?.icon?.url && (
            <Box sx={styles.wrapper()}>
              <Box sx={styles.iconImageContainer({ isSelected })}>
                <SvgOrImg
                  type={option?.icon?.type}
                  src={option.icon.url}
                  alt={option?.icon?.alt || option?.label}
                />
                {isPremium && (
                  <Box sx={styles.premiumOption({ isSelected })}>
                    <span>{premium}</span>
                  </Box>
                )}
                {newColor && (
                  <Box sx={styles.newColorOptions({ isSelected })}>
                    <span>{colorValue}</span>
                  </Box>
                )}
              </Box>
            </Box>
          )}

          {option?.css && (
            <Box
              sx={styles.iconImageContainer({
                isSelected,
                background: option.css.background,
                UIVersion,
              })}
            >
              {isSelected && <CheckmarkCircleIcon />}
            </Box>
          )}
        </ThemeUiButton>
      ),
    };
  });

  const setName = name?.split("--")[0];
  const selectedOptionIsSameAsSet =
    isNil(userChoiceValues[name]) ||
    userChoiceValues[name] === userChoiceValues[setName];

  const handleResetColorOption = () => {
    if (selectedOptionIsSameAsSet) return;
    dispatch(updateUserChoiceValues({ name, value: null }));
  };

  const resetColorButton = (
    <Button
      variant="secondary"
      size={isMobile ? "medium" : "large"}
      isRound
      isSelected={selectedOptionIsSameAsSet}
      onClick={handleResetColorOption}
    >
      {`Same as set ${!selectedOptionIsSameAsSet ? "?" : ""}`}
    </Button>
  );

  return (
    <>
      {UIVersion === 2 && (
        <Box sx={styles.backButtonContainer}>{backButton}</Box>
      )}
      <Box sx={styles.iconButtonOptions({ UIVersion })}>
        {UIVersion === 1 && backButton}
        {resetButtonOption && resetColorButton}
        {optionButtons.map((option) => {
          if (!option.toolTip) {
            return option.component;
          }
          return (
            <ToolTip key={option.key} toolTip={option.toolTip}>
              {option.component}
            </ToolTip>
          );
        })}
      </Box>
    </>
  );
}
